<template>
  <div class="rounded-lg border border-gray-200">
    <div
      class="flex cursor-pointer items-center justify-between bg-white px-4 py-2 shadow-sm transition-colors"
      data-cy="funder-placement-table-header"
      @click="toggleCollapsed"
    >
      <div class="flex items-center gap-4">
        <img
          v-if="funder.logo?.file_url"
          :src="funder.logo.file_url"
          :alt="funder.full_name"
          class="h-8 w-8 rounded object-contain"
        />
        <div class="flex items-center space-x-4">
          <span class="text-sm font-bold text-headline">
            {{ funder.full_name }}
          </span>
        </div>
        <div
          v-show="isCollapsed && aggregatedScorecardResults.length"
          class="flex items-center gap-2 flex-wrap"
        >
          <funder-scorecard-tag
            v-for="result in aggregatedScorecardResults"
            :key="result.id"
            :scorecard-result="result"
          />
        </div>
      </div>
      <icon-chevron-right
        class="m-4 transform cursor-pointer"
        :class="isCollapsed ? 'rotate-270' : 'rotate-90'"
      />
    </div>
    <div v-show="!isCollapsed" class="transition-all duration-200">
      <lf-table
        ref="funderTable"
        v-model:pin-enabled="pinEnabled"
        :data="offerTemplates"
        :columns="columns"
        :checked-rows="initialSelectedRows"
        :lazy-load="false"
        :select-all="canCreatePlacement"
        :freeze-first-two-columns-when-pinned="canCreatePlacement"
        custom-id="funder-placement-table"
        data-cy="lf-table"
        pin-option
        header-sticky
        @selected-rows="handleSelectedRows"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, provide, ref, toRef, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

import compact from "lodash/compact";

import { useFunderTableData } from "@/composables/useFunderTableData";
import { PRODUCT_NAME_TO_ID_MAP } from "@/helpers/constants";
import { hasSubmissionPath } from "@/helpers/services";
import { PlacementFunderKey } from "@/helpers/symbols";
import { useOfferTemplatesQuery } from "@/hooks/queries/offerTemplates";
import { useActiveWorkflowTemplate } from "@/hooks/workflow";

import ClientFunderProductDropdown from "@/components/deals/placement/FunderProductDropdown.vue";
import FunderScorecardKnockouts from "@/components/deals/placement/FunderScorecardKnockouts.vue";
import FunderScorecardResults from "@/components/deals/placement/FunderScorecardResults.vue";
import FunderScorecardRiskTiers from "@/components/deals/placement/FunderScorecardRiskTiers.vue";
import FunderScorecardStatuses from "@/components/deals/placement/FunderScorecardStatuses.vue";
import FunderScorecardTag from "@/components/deals/placement/FunderScorecardTag.vue";
import PlacementFundersTableWorkflowTemplateField from "@/components/deals/placement/PlacementFundersTableWorkflowTemplateField.vue";
import SubmissionPath from "@/components/deals/placement/SubmissionPath.vue";
import IconChevronRight from "@/components/icons/IconChevronRight.vue";
import LfTable from "@/components/ui/table/LfTable.vue";

import type { IDealsColumn } from "@/models/applications";
import type { placementMethod } from "@/models/common";
import type { IFunder, OfferTemplateRecord } from "@/models/funders";
import type {
  EnrichedTableRow,
  PlacementFilters,
  PlacementPresetsMap,
  RowState,
} from "@/models/placement";
import type { IScorecardResult } from "@/models/scorecards";
import type { FinProduct } from "@/models/workflows";

type Props = {
  funder: IFunder;
  canCreatePlacement: boolean;
  funderPlacementPresetsMap?: PlacementPresetsMap;
  scorecardResults?: IScorecardResult[];
  initialSelectedRows?: string[];
  initialRowStates?: Record<string, RowState>;
  filters?: PlacementFilters;
};

const {
  funder,
  canCreatePlacement,
  funderPlacementPresetsMap,
  scorecardResults,
  initialSelectedRows = [],
  initialRowStates = {},
  filters,
} = defineProps<Props>();

const emit = defineEmits<{
  selectedRows: [
    string,
    string[],
    Record<string, RowState>,
    EnrichedTableRow[],
  ];
}>();

provide(PlacementFunderKey, funder);

const { t } = useI18n();
const { allSelectedOfferFunders } = useActiveWorkflowTemplate();

const isCollapsedOverride = ref<boolean | null>(null);
const pinEnabled = ref(true);
const funderTable = useTemplateRef("funderTable");

const isServiceWithSubmissionPath = computed(() => hasSubmissionPath(funder));

const isCollapsed = computed(() => {
  if (isCollapsedOverride.value !== null) {
    return isCollapsedOverride.value;
  }
  const hasAnyScorecardResults = offerTemplates.value.some(
    (template) => template.score_card_results?.length
  );
  return !hasAnyScorecardResults;
});

const toggleCollapsed = () => {
  isCollapsedOverride.value = !isCollapsed.value;
};

const aggregatedScorecardResults = computed(() => {
  const seenScorecardIds = new Set<string>();

  return offerTemplates.value
    .flatMap(({ score_card_results }) => score_card_results || [])
    .reduce<IScorecardResult[]>((results, result) => {
      if (!seenScorecardIds.has(result.score_card_id)) {
        seenScorecardIds.add(result.score_card_id);
        results.push(result);
      }
      return results;
    }, []);
});

const templateIds = computed(() => {
  const currentFunder = allSelectedOfferFunders.value.find(
    (funderRecord) => funderRecord.id === funder.id
  );

  if (!currentFunder) {
    return [];
  }

  return currentFunder.offer_templates.reduce((acc: string[], template) => {
    if (template.enabled) {
      acc.push(template.id);
    }
    return acc;
  }, []);
});

const offerTemplatesQuery = useOfferTemplatesQuery(() => ({
  ids: templateIds.value,
}));

const rowStates = ref<
  Record<
    string,
    {
      selected_method: placementMethod;
      workflow_template_id?: string;
      client_offer_template?: string;
      submission_path?: string;
    }
  >
>({ ...initialRowStates });

const allOfferTemplates = useFunderTableData(
  funder,
  offerTemplatesQuery.data,
  rowStates,
  toRef(() => funderPlacementPresetsMap ?? {}),
  toRef(() => scorecardResults ?? [])
);

const calculateScorecardScore = (result: IScorecardResult): number => {
  const points = result.points ?? -1;
  const totalPoints = result.score_card?.points;
  const denominator =
    typeof totalPoints === "number" && totalPoints !== 0 ? totalPoints : 1;
  return points / denominator;
};

const sortByScorecardScore = (a: EnrichedTableRow, b: EnrichedTableRow) => {
  const hasScoreA = a.score_card_results?.length;
  const hasScoreB = b.score_card_results?.length;

  if (hasScoreA && !hasScoreB) {
    return -1;
  }
  if (!hasScoreA && hasScoreB) {
    return 1;
  }

  if (hasScoreA && hasScoreB && a.score_card_results && b.score_card_results) {
    const scoreA = calculateScorecardScore(a.score_card_results[0]);
    const scoreB = calculateScorecardScore(b.score_card_results[0]);

    if (scoreA !== scoreB) {
      return scoreB - scoreA;
    }
  }

  const productA = a.offer_template?.product ?? "";
  const productB = b.offer_template?.product ?? "";

  return productA.localeCompare(productB);
};

const offerTemplates = computed(() => {
  const templates = !filters?.selectedProductTypes?.length
    ? allOfferTemplates.value
    : allOfferTemplates.value.filter((template) => {
        const templateProduct = template.offer_template?.product;

        if (!templateProduct) {
          return true;
        }

        const templateProductId =
          PRODUCT_NAME_TO_ID_MAP[templateProduct.toLowerCase()];
        if (!templateProductId) {
          return true;
        }

        return filters.selectedProductTypes.includes(
          templateProductId as FinProduct
        );
      });

  return templates.sort(sortByScorecardScore);
});

const cleanupRowStates = () => {
  const currentRowIds = new Set(
    allOfferTemplates.value.map((template) => `${funder.id}_${template.id}`)
  );
  Object.keys(rowStates.value).forEach((rowId) => {
    if (!currentRowIds.has(rowId)) {
      delete rowStates.value[rowId];
    }
  });
};

const syncPresetValuesToRowStates = () => {
  allOfferTemplates.value.forEach((template) => {
    const rowId = template.id;

    if (!rowStates.value[rowId]) {
      const hasPresetValues =
        template.workflow_template_id ||
        template.client_offer_template ||
        template.selected_method !== funder.primary_method;

      if (hasPresetValues) {
        rowStates.value[rowId] = {
          selected_method: template.selected_method,
          workflow_template_id: template.workflow_template_id,
          client_offer_template: template.client_offer_template,
        };
      }
    }
  });
};
const updateFunderRecord =
  (propSelector: keyof IFunder) =>
  (rowId: string, value: string | OfferTemplateRecord) => {
    if (typeof value !== "string") {
      return;
    }

    if (!rowStates.value[rowId]) {
      rowStates.value[rowId] = {
        selected_method: funder.selected_method || 1,
      };
    }

    const validFields = [
      "workflow_template_id",
      "client_offer_template",
      "submission_path",
    ];
    if (validFields.includes(propSelector)) {
      const field = propSelector as
        | "workflow_template_id"
        | "client_offer_template"
        | "submission_path";
      rowStates.value[rowId][field] = value;
    }
  };

const columns = computed(
  () =>
    compact([
      {
        label: t("COMMON.PRODUCT"),
        key: "name",
      },
      {
        label: t("DEALS.PLACEMENT.TABLE_COLUMNS.SCORECARD_STATUS"),
        key: "score_card_results",
        component: FunderScorecardStatuses,
      },
      {
        label: t("DEALS.PLACEMENT.TABLE_COLUMNS.SCORECARD_RESULT", 2),
        key: "score_card_results",
        component: FunderScorecardResults,
      },
      {
        label: t("DEALS.PLACEMENT.TABLE_COLUMNS.SUBMISSION"),
        key: "selected_method",
        component: "placement-method-dropdown",
        eventHandlers: {
          methodChange: (
            rowId: string,
            selectedSubmissionType: placementMethod
          ) => {
            if (!rowStates.value[rowId]) {
              rowStates.value[rowId] = {
                selected_method: selectedSubmissionType,
              };
            } else {
              rowStates.value[rowId].selected_method = selectedSubmissionType;
            }
          },
        },
      },
      isServiceWithSubmissionPath.value
        ? {
            label: t("PLACEMENT.ONDECK.SUBMISSION_PATH"),
            key: "submission_path",
            component: SubmissionPath,
            eventHandlers: {
              change: updateFunderRecord("submission_path"),
            },
          }
        : null,
      {
        label: t("COMMON.WORKFLOW_BUILDER_TEMPLATE"),
        key: "workflow_template_id",
        component: PlacementFundersTableWorkflowTemplateField,
        eventHandlers: { change: updateFunderRecord("workflow_template_id") },
      },
      {
        label: t("OFFER_CUSTOMISATION.FUNDER_PRODUCT"),
        key: "client_offer_template",
        component: ClientFunderProductDropdown,
        eventHandlers: {
          change: updateFunderRecord("client_offer_template"),
        },
        props: {
          teleportOptions: {
            enabled: true,
            scrollable: "dealsWrapper",
            horizontalScrollable: "funder-placement-table",
          },
          asBadge: true,
        },
      },

      {
        label: t("DEALS.PLACEMENT.TABLE_COLUMNS.SCORECARD_KNOCKOUT"),
        key: "score_card_results",
        component: FunderScorecardKnockouts,
      },
      {
        label: t("DEALS.PLACEMENT.TABLE_COLUMNS.SCORECARD_RISK_TIER"),
        key: "score_card_results",
        component: FunderScorecardRiskTiers,
      },
      {
        label: t("DEALS.PLACEMENT.TABLE_COLUMNS.RISK_TIER_FUNDERS"),
        key: "risk_tiers",
        component: "risk-tier-list",
      },
    ]) as IDealsColumn[]
);

const handleSelectedRows = (selectedRowIds: string[]) => {
  const filteredRows = offerTemplates.value.filter((t) =>
    selectedRowIds.includes(t.id)
  );
  emit(
    "selectedRows",
    funder.id,
    selectedRowIds,
    rowStates.value,
    filteredRows
  );
};

watch(() => allOfferTemplates.value.length, cleanupRowStates, {
  flush: "post",
});
watch(() => allOfferTemplates.value, syncPresetValuesToRowStates, {
  flush: "post",
  deep: true,
});

defineExpose({
  funder,
  offerTemplates,
  setSelectedIds: (ids: string[]) => {
    if (!funderTable.value) {
      return;
    }
    funderTable.value.selectedRows = ids;
  },
  resetToPresets: () => {
    rowStates.value = {};
    syncPresetValuesToRowStates();
  },
  collapseTable: () => {
    isCollapsedOverride.value = true;
  },
});
</script>
